import { Injectable } from '@angular/core';
import {
    ICheckpointAction,
    ICheckpointActionAlertRecipient,
    ICheckpointCondition,
    ICheckpointConditionTypeOption,
    ICheckpointRange,
    ICheckpointV2,
} from './checklist-v2';
import {
    Checkpoint2MeasureUnitType,
    Checkpoint2StatusType,
    CheckpointActionConditionType,
    CheckpointActionLocationType,
    CheckpointActionRecipientType,
    CheckpointActionTimePeriodType,
    CheckpointActionType,
    CheckpointConditionTargetType,
    CheckpointConditionType,
    CheckpointRangeType,
    CheckpointScoringType,
    CheckpointType,
} from './checklist-enum';
import { cloneDeep, isDefined } from '../utils';
import {
    Checkpoint2MeasureUnitTypes,
    CheckpointMeasurementConditionTypeDefaultOptions,
    CheckpointRangeTypesOptions,
    CheckpointScorings,
    CheckpointStatusConditionTypeOptions,
} from './checklist-consts';
import { INameIdObj } from '../fi-shared-interfaces';
import { isNullOrUndefined } from 'util';
import { IAccessRightFilterCollection } from '../models';
import { IFiDialogResult } from '../fi-dialogs/fi-dialogs';
import { FiCheckpointActionNotificationEditorDialogComponent } from './fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/actions-tab/action-notification-editor-dialog/action-notification-editor-dialog.component';
import { FiCheckpointActionTaskEditorDialogComponent } from './fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/actions-tab/action-task-editor-dialog/action-task-editor-dialog.component';
import { IContentLanguageLabelModel } from '../fi-content-language-shared/fi-content-language-shared';
import { FiContentLanguageSharedService } from '../fi-content-language-shared/fi-content-language-shared.service';
import { AppTextService } from '../app-text';
import { FiDialogsService } from '../fi-dialogs';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiTaskPriorityType } from '@fi-app/task-v2/fi-task-v2-enums';

@Injectable()
export class FiCheckpoint2HelperService {
    constructor(
        private appTextService: AppTextService,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
        private fiDialogsService: FiDialogsService,
    ) {}

    private rangeUid = 100;
    private getUniqueId(): number {
        return ++this.rangeUid;
    }

    getContentLanguageLabelModels(
        checklistLangLabelModels?: IContentLanguageLabelModel[],
    ): IContentLanguageLabelModel[] {
        const langLabelModels: IContentLanguageLabelModel[] = [];
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(
            langLabelModels,
            checklistLangLabelModels ? checklistLangLabelModels : [],
        );
        return langLabelModels;
    }

    getCheckpointInstanceV2(): ICheckpointV2 {
        return {
            MediaBankFileReferences: [],
            Names: this.getContentLanguageLabelModels(),
            Descriptions: this.getContentLanguageLabelModels(),
            Scoring: false,
            Ranges: [this.getInitialRange(CheckpointType.Status)],
            Conditions: [],
            Actions: [],
            Type: CheckpointType.Status,
        };
    }

    getCheckpointCopyInstanceV2(checkpointOrig: ICheckpointV2): ICheckpointV2 {
        const checkpoint: ICheckpointV2 = cloneDeep(checkpointOrig);
        delete checkpoint.Id;
        checkpoint.Names.forEach((item) => {
            if (item.Value) {
                const copyText = item.ContentLanguageId === FiApplicationLanguageType.Swedish ? 'Kopia' : 'Copy';
                item.Value = `(${copyText}) ` + item.Value;
            }
            delete item.Id;
        });
        checkpoint.Descriptions.forEach((item) => delete item.Id);
        checkpoint.Ranges.forEach((item) => delete item.Id);
        checkpoint.Conditions.forEach((item) => delete item.Id);
        checkpoint.Actions.forEach((item) => delete item.Id);
        checkpoint.MediaBankFileReferences.forEach((item) => delete item.Id);
        if (checkpoint.ScoringData) {
            delete checkpoint.ScoringData.Id;
        }
        return checkpoint;
    }

    getDefaultRange(): ICheckpointRange {
        return {
            Type: undefined,
            CheckpointStatus: undefined,
            MinValue: undefined,
            MaxValue: undefined,
            uid: this.getUniqueId(),
        };
    }

    readonly initialRangeMinValue = 0;
    readonly initialRangeMaxValue = 999;
    getInitialRange(checkpointType: CheckpointType): ICheckpointRange {
        const checkpointRange: ICheckpointRange = this.getDefaultRange();
        checkpointRange.Type = CheckpointRangeType.Between;
        checkpointRange.CheckpointStatus = Checkpoint2StatusType.Ok;
        if (checkpointType === CheckpointType.Status) {
            checkpointRange.MinValue = 1;
            checkpointRange.MaxValue = 1;
        } else {
            checkpointRange.MinValue = this.initialRangeMinValue;
            checkpointRange.MaxValue = this.initialRangeMaxValue;
        }
        return checkpointRange;
    }

    getDefaultCondition(): ICheckpointCondition {
        return {
            Type: undefined,
            MinValue: undefined,
            MaxValue: undefined,
            TargetType: undefined,
        };
    }

    getCheckpointConditionTypeQRCodeOptions(): ICheckpointConditionTypeOption[] {
        // for qr codes only "always" type is available
        return this.getDefaultCheckpointConditionTypeOptions(
            CheckpointStatusConditionTypeOptions,
            CheckpointType.Status,
        ).filter((option) => option.Id === CheckpointConditionType.Always);
    }

    getCheckpointConditionTypeOptions(
        checkpointType: CheckpointType,
        checkpointRanges: ICheckpointRange[],
    ): ICheckpointConditionTypeOption[] {
        if (checkpointType === CheckpointType.Status) {
            return this.getDefaultCheckpointConditionTypeOptions(CheckpointStatusConditionTypeOptions, checkpointType);
        }
        const conditionTypeOptions = this.getDefaultCheckpointConditionTypeOptions(
            CheckpointMeasurementConditionTypeDefaultOptions,
            checkpointType,
        );
        /*checkpointRanges
            .filter(range => !this.isRangeNotFinished(range))
            .forEach((range) => {
                conditionTypeOptions.push({
                    Id: range.Type,
                    Name: this.appTextService.get('checklist.checkpoint_condition_reported_value_is', {
                        '{0}': this.getRangeLabel(range)
                    }),
                    RangeUid: range.uid,
                    RangeId: range.Id
                });
            });*/
        return conditionTypeOptions;
    }

    private isRangeNotFinished(range: ICheckpointRange): boolean {
        if (!isDefined(range)) return true;
        if (!isDefined(range.Type)) return true;
        // Between is not finished
        if (range.Type === CheckpointRangeType.Between && (!isDefined(range.MinValue) || !isDefined(range.MaxValue)))
            return true;
        // Greater than is not finished
        if (range.Type === CheckpointRangeType.Greater && !isDefined(range.MinValue)) return true;
        // Less than is not finished
        return range.Type === CheckpointRangeType.Less && !isDefined(range.MaxValue);
    }

    private getRangeLabel(range: ICheckpointRange): string {
        const rangeTypeName = this.appTextService
            .get(
                CheckpointRangeTypesOptions.find((option) => {
                    return option.Id === range.Type;
                })?.Name,
            )
            .toLowerCase();
        const rangeValueText = this.getRangeValueText(range);
        return `${rangeTypeName} ${rangeValueText}`;
    }

    private getRangeValueText(range: ICheckpointRange) {
        if (range.Type === CheckpointRangeType.Between) return `${range.MinValue} - ${range.MaxValue}`;

        if (range.Type === CheckpointRangeType.Greater) return `${range.MinValue}`;

        if (range.Type === CheckpointRangeType.Less) return `${range.MaxValue}`;
    }

    private getDefaultCheckpointConditionTypeOptions(
        defaultOptions: INameIdObj[],
        checkpointType: CheckpointType,
    ): ICheckpointConditionTypeOption[] {
        const defaultCheckpointConditionTypeOptions = [];
        defaultOptions.forEach((option) => {
            defaultCheckpointConditionTypeOptions.push({
                Id: option.Id,
                Name:
                    option.Id === CheckpointConditionType.Always
                        ? this.appTextService.get(option.Name)
                        : this.appTextService.get(
                              checkpointType === CheckpointType.Status
                                  ? 'checklist.checkpoint_condition_answer'
                                  : 'checklist.checkpoint_condition_reported_value_is',
                              {
                                  '{0}': this.appTextService.get(option.Name),
                              },
                          ),
            });
        });
        return defaultCheckpointConditionTypeOptions;
    }

    getDefaultAction(checkpointActionType: CheckpointActionType, isComplianceThreshold?: boolean): ICheckpointAction {
        const checkpointAction: ICheckpointAction = {
            ActionCondition: undefined,
            Action: checkpointActionType,
            Settings: {
                AlertRecipient: this.getDefaultAlertRecipient(),
                TaskPriority: FiTaskPriorityType.Medium,
                TaskHasDeadline: false,
                TaskDeadlineTimePeriod: CheckpointActionTimePeriodType.Days,
                TaskDeadlineValue: 2,
            },
        };

        checkpointAction.ActionCondition = isComplianceThreshold
            ? CheckpointActionConditionType.ComplianceThreshold
            : CheckpointActionConditionType.NotOk;
        checkpointAction.Settings.AlertRecipient.RecipientType = CheckpointActionRecipientType.RolesLocal;

        if (checkpointActionType === CheckpointActionType.CreateTask) {
            checkpointAction.Settings.TaskName = '';
            checkpointAction.Settings.TaskDescription = '';
            //checkpointAction.Settings.TaskMediaBankFileReferences = [];
        } else {
            checkpointAction.Settings.AlertRecipient.ViaNotification = true;
        }

        return checkpointAction;
    }

    private getDefaultAlertRecipient(): ICheckpointActionAlertRecipient {
        return {
            RecipientType: undefined,
            LocationType: CheckpointActionLocationType.OnlyCurrent,
            UserIds: [],
            RoleIds: [],
            UnitIds: [],
            UserContacts: [],
            ViaNotification: false,
            ViaSMS: false,
            ViaEmail: false,
        };
    }

    getCheckpointStatus(answerValue: number, ranges: ICheckpointRange[]): Checkpoint2StatusType {
        // todo if here will be changes - synchronise with backend
        if (isNullOrUndefined(answerValue)) {
            return Checkpoint2StatusType.NotFilledOut;
        }
        let status = Checkpoint2StatusType.NotOk;
        let rangeFound = false;
        for (let idx = 0; idx < ranges.length && !rangeFound; idx++) {
            const range = ranges[idx];
            rangeFound =
                (range.Type === CheckpointRangeType.Between &&
                    range.MinValue <= answerValue &&
                    answerValue <= range.MaxValue) ||
                (range.Type === CheckpointRangeType.Greater && range.MinValue <= answerValue) ||
                (range.Type === CheckpointRangeType.Less && answerValue <= range.MaxValue);
            if (rangeFound) {
                status = range.CheckpointStatus;
            }
        }
        return status;
    }

    getCheckpointTaskNameByActionCondition(
        actionCondition: CheckpointActionConditionType,
        checkpointName: string,
        checklistName: string,
    ): string {
        const checklistLabel = !!checklistName?.length
            ? checklistName
            : this.getNormalizedTaskNamePart('checklist.checkpoint_action_task_checklist');
        const checkpointLabel = !!checkpointName?.length
            ? checkpointName
            : this.getNormalizedTaskNamePart('checklist.checkpoint_action_task_checkpoint');

        if (actionCondition === CheckpointActionConditionType.NotFilledOut) {
            return this.appTextService.get('checklist.checkpoint_action_task_description_skipped', {
                '{0}': this.getNormalizedTaskNamePart('checklist.checkpoint_action_task_unit_name'),
                '{1}': checkpointLabel,
                '{2}': checklistLabel,
            });
        }
        return this.appTextService.get('checklist.checkpoint_action_task_description_answered', {
            '{0}': this.getNormalizedTaskNamePart('checklist.checkpoint_action_task_staff_name'),
            '{1}': this.getNormalizedTaskNamePart('checklist.checkpoint_action_task_unit_name').toLocaleLowerCase(),
            '{2}': checkpointLabel,
            '{3}': checklistLabel,
        });
    }

    private getNormalizedTaskNamePart(textId: string) {
        // https://chainformation.atlassian.net/browse/FI-5875
        return this.appTextService.get(textId).replace('{', '').replace('}', '');
    }

    getCheckpointActionEditorDialogByType(
        checkpointAction: ICheckpointAction,
        accessRightFilterCollection: IAccessRightFilterCollection,
        complianceThresholdMode: boolean,
        checkpointName: string,
        checklistName: string,
    ): IFiDialogResult<ICheckpointAction> {
        return checkpointAction.Action === CheckpointActionType.CreateTask
            ? this.openCheckpointTaskActionEditorDialog(
                  checkpointAction,
                  accessRightFilterCollection,
                  checkpointName,
                  checklistName,
              )
            : this.openCheckpointNotificationActionEditorDialog(
                  checkpointAction,
                  accessRightFilterCollection,
                  complianceThresholdMode,
                  checkpointName,
                  checklistName,
              );
    }

    openCheckpointNotificationActionEditorDialog(
        action: ICheckpointAction,
        accessRightFilterCollection: IAccessRightFilterCollection,
        complianceThresholdMode: boolean,
        checkpointName: string,
        checklistName: string,
    ): IFiDialogResult<ICheckpointAction> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiCheckpointActionNotificationEditorDialogComponent,
            contentComponentData: {
                action,
                accessRightFilterCollection,
                complianceThresholdMode,
                checkpointName,
                checklistName,
            },
            hideDialogTopBorder: true,
            headerText: this.appTextService.get('checklist.checkpoint_action_send_notification'),
            preventUppercaseButtonLabel: true,
            isHeaderDividerShown: true,
            headerDividerClass: 'BrandedBorderColor',
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
                autoFocus: false,
                minWidth: '50%',
                closeOnNavigation: false,
            },
        });
    }

    private openCheckpointTaskActionEditorDialog(
        action: ICheckpointAction,
        accessRightFilterCollection: IAccessRightFilterCollection,
        checkpointName: string,
        checklistName: string,
    ): IFiDialogResult<ICheckpointAction> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiCheckpointActionTaskEditorDialogComponent,
            contentComponentData: {
                action,
                accessRightFilterCollection,
                complianceThresholdMode: false,
                checkpointName,
                checklistName,
            },
            hideDialogTopBorder: true,
            headerText: this.appTextService.get('checklist.checkpoint_action_automated_task'),
            preventUppercaseButtonLabel: true,
            isHeaderDividerShown: true,
            headerDividerClass: 'BrandedBorderColor',
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
                autoFocus: false,
                maxWidth: '55%',
                closeOnNavigation: false,
            },
        });
    }

    getConfirmDeleteCheckpointActionsDialog() {
        return this.fiDialogsService.confirmSimpleDelete({
            headerText: this.appTextService.get('checklist.delete_automated_action'),
            messageText: this.appTextService.get('checklist.delete_automated_action_message'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-confirm-delete-dialog-panel', 'fi-rounded-dialog-panel'],
                autoFocus: false,
            },
        });
    }

    getCheckpointTaskRecipientIds(alertRecipient: ICheckpointActionAlertRecipient): number[] {
        if (
            alertRecipient.RecipientType === CheckpointActionRecipientType.Roles ||
            alertRecipient.RecipientType === CheckpointActionRecipientType.RolesLocal
        )
            return alertRecipient.RoleIds;

        if (alertRecipient.RecipientType === CheckpointActionRecipientType.Users) {
            return alertRecipient.UserIds;
        }
    }

    getQRCodeForCheckpoint(checkpoint: ICheckpointV2) {
        return `checkpointV2:${checkpoint.Id}`;
    }

    getCheckpointMeasureUnitShortText(measureUnitType: Checkpoint2MeasureUnitType) {
        if (!measureUnitType) return '';
        const checkpoint2MeasureUnitType = Checkpoint2MeasureUnitTypes.find((item) => item.id === measureUnitType);
        if (checkpoint2MeasureUnitType) {
            if (checkpoint2MeasureUnitType.shortTextId)
                return this.appTextService.get(checkpoint2MeasureUnitType.shortTextId);

            return checkpoint2MeasureUnitType.shortText;
        }
        throw new Error(`wrong measureUnitType (${measureUnitType}) in getCheckpointMeasureUnitShortText`);
    }

    getScoringText(scoringType: CheckpointScoringType): string {
        if (isNullOrUndefined(scoringType)) return '';
        const currentCheckpointScoring = CheckpointScorings.find((item) => item.id === scoringType);
        if (currentCheckpointScoring) {
            return this.appTextService.get(currentCheckpointScoring.textId);
        }
        throw new Error(`unknown scoringType ${scoringType} in getScoringText`);
    }

    isConditionDisabledByQRCode(targetType: CheckpointConditionTargetType, isQRCodeEnabled: boolean) {
        return targetType === CheckpointConditionTargetType.QRCode && !isQRCodeEnabled;
    }
}
